/**
 * `/settings` — Darstellung and Konto, and nothing else.
 *
 * There is deliberately no greyed-out "Einheiten (später)" row: a disabled
 * future feature is a subscription tease, and units arrive when they arrive.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppShell } from '../app/AppShell';
import { PageHeader } from '../app/PageHeader';
import { useTheme, type Theme } from '../app/theme';
import { ChangePasswordDialog } from '../components/Account';
import { useLang, useT, type Lang } from '../i18n';
import { useSession } from '../session';

const THEME_LABEL: Record<Theme, string> = {
  system: 'settings.theme.system',
  light: 'settings.theme.light',
  dark: 'settings.theme.dark',
};

export default function Settings() {
  const t = useT();
  const { lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();
  const session = useSession();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(false);
  const [changed, setChanged] = useState(false);

  return (
    <AppShell active="settings">
      <div className="ae-page">
        <PageHeader title={t('settings.title')} />

        <section className="ae-panel">
          <div className="ae-panel-head">
            <h2>{t('settings.appearance')}</h2>
          </div>
          <div className="ae-rows">
            <div className="ae-row">
              <div className="ae-row-main">
                <div className="ae-row-label">{t('settings.language')}</div>
              </div>
              <div className="ae-seg" role="group" aria-label={t('settings.language')}>
                {(['de', 'en'] as Lang[]).map(l => (
                  <button
                    key={l}
                    type="button"
                    aria-pressed={lang === l}
                    onClick={() => setLang(l)}
                  >
                    {l === 'de' ? 'Deutsch' : 'English'}
                  </button>
                ))}
              </div>
            </div>
            <div className="ae-row">
              <div className="ae-row-main">
                <div className="ae-row-label">{t('settings.theme')}</div>
              </div>
              <div className="ae-seg" role="group" aria-label={t('settings.theme')}>
                {(['system', 'light', 'dark'] as Theme[]).map(x => (
                  <button
                    key={x}
                    type="button"
                    aria-pressed={theme === x}
                    onClick={() => setTheme(x)}
                  >
                    {t(THEME_LABEL[x])}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ae-panel">
          <div className="ae-panel-head">
            <h2>{t('settings.account')}</h2>
          </div>
          {session.user ? (
            <div className="ae-rows">
              <div className="ae-row">
                <div className="ae-row-main">
                  <div className="ae-row-label">{session.user.email}</div>
                  {changed ? <div className="ae-row-note">{t('pw.changed')}</div> : null}
                </div>
                <button type="button" className="ae-btn" onClick={() => setDialog(true)}>
                  {t('pw.change')}
                </button>
              </div>
              <div className="ae-row">
                <div className="ae-row-main" />
                <button
                  type="button"
                  className="ae-btn"
                  onClick={() => void session.signOut().then(() => navigate('/login'))}
                >
                  {t('nav.signout')}
                </button>
              </div>
            </div>
          ) : (
            <div className="ae-rows">
              <div className="ae-row">
                <div className="ae-row-main">
                  <div className="ae-row-label">{t('settings.signedout')}</div>
                </div>
                <Link className="ae-btn ae-btn-primary" to="/login">
                  {t('nav.signin')}
                </Link>
              </div>
            </div>
          )}
        </section>

        {dialog ? (
          <ChangePasswordDialog
            onClose={() => setDialog(false)}
            onDone={() => {
              setDialog(false);
              setChanged(true);
            }}
          />
        ) : null}
      </div>
    </AppShell>
  );
}
